import { z } from 'zod';
import {
  sanitizePlainText,
  stripCoordString,
  LAT_MIN,
  LAT_MAX,
  LNG_MIN,
  LNG_MAX,
  MAX_LAT_INPUT_LEN,
  MAX_LNG_INPUT_LEN
} from '@/utils/sanitizeFormInput.js';

export const CUSTOMER_PRESETS = ['Airtel', 'Jio', 'BSNL'];

export const SOUTH_INDIA_STATES = [
  'Andhra Pradesh',
  'Telangana',
  'Karnataka',
  'Tamil Nadu',
  'Kerala',
  'Goa',
  'Puducherry'
];

const t3 = z.preprocess(
  (v) => sanitizePlainText(String(v ?? ''), 100),
  z.string().trim().min(3, 'Minimum 3 characters').max(100, 'Maximum 100 characters')
);

// Polinaidu: 21-04-2026 (21:25) - Created optional version of t3 for optional inputs
const t3Optional = z.preprocess(
  (v) => sanitizePlainText(String(v ?? ""), 100),
  z.string().trim().max(100, "Maximum 100 characters").optional().default(""),
);

const MAX_HEIGHT_METERS = 100000;

// Polinaidu: 22-04-2026 (13:48) - Made optional as per user request
const latStr = z.preprocess(
  (v) => stripCoordString(String(v ?? ''), true, MAX_LAT_INPUT_LEN),
  z
    .string()
    .optional()
    .default('')
    .refine((s) => s === '' || /^-?\d*\.?\d+$/.test(s), 'Numbers only (decimals allowed)')
    .refine((s) => {
      if (s === '') return true;
      const n = Number(s);
      return Number.isFinite(n) && n >= LAT_MIN && n <= LAT_MAX;
    }, `Latitude must be between ${LAT_MIN} and ${LAT_MAX}`)
);

// Polinaidu: 22-04-2026 (13:48) - Made optional as per user request
const lngStr = z.preprocess(
  (v) => stripCoordString(String(v ?? ''), true, MAX_LNG_INPUT_LEN),
  z
    .string()
    .optional()
    .default('')
    .refine((s) => s === '' || /^-?\d*\.?\d+$/.test(s), 'Numbers only (decimals allowed)')
    .refine((s) => {
      if (s === '') return true;
      const n = Number(s);
      return Number.isFinite(n) && n >= LNG_MIN && n <= LNG_MAX;
    }, `Longitude must be between ${LNG_MIN} and ${LNG_MAX}`)
);

function parseYmd(s) {
  if (!s) return null;
  const p = String(s).split('-').map(Number);
  if (p.length !== 3 || p.some((n) => !Number.isFinite(n))) return null;
  return new Date(p[0], p[1] - 1, p[2], 0, 0, 0, 0);
}

function startOfToday() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 0, 0, 0, 0);
}

export const projectSchema = z
  .object({
    _mode: z.enum(['create', 'edit']).default('create'),
    // Polinaidu: 21-04-2026 (20:47) - Make projectId optional as backend now generates it automatically.
    projectId: z.preprocess(
      (v) => sanitizePlainText(String(v ?? ""), 100),
      z.string().trim().max(100).optional().default(""),
    ),
    customerPreset: z.string().min(1, 'Select a customer'),
    projectName: t3,
    applicationId: t3,
    srType: t3Optional,
    siteId: t3,
    siteName: t3,
    applicationDate: z.string().min(1, 'Application date is required'),
    line1: t3Optional,
    line2: z.preprocess(
      (v) => sanitizePlainText(String(v ?? ''), 100),
      z.string().optional().default('')
    ),
    city: t3,
    state: z.enum(SOUTH_INDIA_STATES, { message: 'Select a valid state' }),
    country: t3Optional,
    latitude: latStr,
    longitude: lngStr,
    customerSiteId: t3,
    district: t3,
    poNumber: t3Optional,
    poStatus: t3Optional,
    poDate: z.string().optional().default(''),
    wccNumber: t3Optional,
    wccStatus: t3Optional,
    invoiceNumber: t3Optional,
    invoiceDate: z.string().optional().default(''),
    invoiceStatus: z.enum(['PAID', 'NOT PAID', 'PARTIAL PAID', '']).optional().default(''),
    commencementDetails: t3Optional,
    remarks: t3Optional,
    siteStatus: z.enum(['WIP', 'WNS', 'HOLD', 'NFA', 'RFAI', '']).optional().default(''),
    structureType: z.enum(['RTT', 'RTP', 'GBT', '']).optional().default(''),
    height: z.preprocess(
      (v) => sanitizePlainText(String(v ?? ''), 24).replace(/[^0-9.]/g, ''),
      z
        .string()
        .trim()
        .optional()
        .default('')
        .refine((s) => s === '' || /^\d+(\.\d+)?$/.test(s), 'Height must be a positive number')
        .refine((s) => s === '' || Number(s) > 0, 'Height must be greater than zero')
        .refine((s) => s === '' || Number(s) <= MAX_HEIGHT_METERS, `Height cannot exceed ${MAX_HEIGHT_METERS} meters`)
    ),
    projectValue: z
      .string({ required_error: 'Total project value is required' })
      .trim()
      .min(1, 'Total project value is required')
      .refine((s) => /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => Number(s) >= 0, 'Cannot be negative'),
    targetCost: z
      .string()
      .trim()
      .optional()
      .default('')
      .refine((s) => s === '' || /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => s === '' || Number(s) >= 0, 'Cannot be negative'),
    civilTargetCost: z
      .string({ required_error: 'Civil target budget is required' })
      .trim()
      .min(1, 'Civil target budget is required')
      .refine((s) => /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => Number(s) >= 0, 'Cannot be negative'),
    electricalTargetCost: z
      .string({ required_error: 'Electrical target budget is required' })
      .trim()
      .min(1, 'Electrical target budget is required')
      .refine((s) => /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => Number(s) >= 0, 'Cannot be negative'),
    mechanicalTargetCost: z
      .string({ required_error: 'Project target budget is required' })
      .trim()
      .min(1, 'Project target budget is required')
      .refine((s) => /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => Number(s) >= 0, 'Cannot be negative'),
    civilExpenseLimit: z
      .string()
      .trim()
      .optional()
      .default('')
      .refine((s) => s === '' || /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => s === '' || Number(s) >= 0, 'Cannot be negative'),
    electricalExpenseLimit: z
      .string()
      .trim()
      .optional()
      .default('')
      .refine((s) => s === '' || /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => s === '' || Number(s) >= 0, 'Cannot be negative'),
    mechanicalExpenseLimit: z
      .string()
      .trim()
      .optional()
      .default('')
      .refine((s) => s === '' || /^\d+(\.\d+)?$/.test(s), 'Must be a valid number')
      .refine((s) => s === '' || Number(s) >= 0, 'Cannot be negative'),
    advancePaid: z.union([z.string(), z.literal('')]).optional().default(''),
    // Polinaidu: 22-04-2026 (13:48) - Made optional as per user request
    startDate: z.string().optional().default(''),
    expectedEndDate: z.string().optional().default(''),
    projectManager: z.string().optional().default(''),
    fieldOfficerIds: z
      .array(z.string().min(1))
      .optional()
      .default(() => []),
    status: z.string().default('Not started'),
    capacity: z.preprocess(
      (v) => sanitizePlainText(String(v ?? ''), 100),
      z.union([z.string(), z.literal('')]).optional().default('')
    ),
    documents: z.any().optional()
  })
  .superRefine((data, ctx) => {
    const pv = data.projectValue === '' ? undefined : Number(data.projectValue);
    const tc = data.targetCost === '' ? undefined : Number(data.targetCost);
    if (pv !== undefined && tc !== undefined && tc > pv) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Internal target cost cannot exceed total project value',
        path: ['targetCost']
      });
    }

    const ctcValue = data.civilTargetCost === '' ? undefined : Number(data.civilTargetCost);
    const etcValue = data.electricalTargetCost === '' ? undefined : Number(data.electricalTargetCost);
    const mtcValue = data.mechanicalTargetCost === '' ? undefined : Number(data.mechanicalTargetCost);

    const sumTradeBudgets = (ctcValue || 0) + (etcValue || 0) + (mtcValue || 0);

    if (pv !== undefined && sumTradeBudgets > pv) {
      if (ctcValue !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Sum of trade budgets cannot exceed PO value',
          path: ['civilTargetCost']
        });
      }
      if (etcValue !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Sum of trade budgets cannot exceed PO value',
          path: ['electricalTargetCost']
        });
      }
      if (mtcValue !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Sum of trade budgets cannot exceed PO value',
          path: ['mechanicalTargetCost']
        });
      }
    }

    const cel = data.civilExpenseLimit === '' ? undefined : Number(data.civilExpenseLimit);
    if (cel !== undefined && cel > (ctcValue || 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ctcValue === undefined ? 'Target budget is required if expense limit is set' : 'Expense limit cannot exceed target budget',
        path: ['civilExpenseLimit']
      });
    }

    const eel = data.electricalExpenseLimit === '' ? undefined : Number(data.electricalExpenseLimit);
    if (eel !== undefined && eel > (etcValue || 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: etcValue === undefined ? 'Target budget is required if expense limit is set' : 'Expense limit cannot exceed target budget',
        path: ['electricalExpenseLimit']
      });
    }

    const mel = data.mechanicalExpenseLimit === '' ? undefined : Number(data.mechanicalExpenseLimit);
    if (mel !== undefined && mel > (mtcValue || 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: mtcValue === undefined ? 'Target budget is required if expense limit is set' : 'Expense limit cannot exceed target budget',
        path: ['mechanicalExpenseLimit']
      });
    }

    const advRaw = data.advancePaid === '' || data.advancePaid == null ? '0' : String(data.advancePaid);
    if (!/^\d+(\.\d+)?$/.test(advRaw.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Advance paid must be a valid number',
        path: ['advancePaid']
      });
    } else {
      const adv = Number(advRaw);
      // Polinaidu: 28-04-2026 (19:26) - Removed check that prevents advancePaid from exceeding projectValue
    }

    const start = data.startDate ? parseYmd(data.startDate) : null; // Polinaidu: 22-04-2026 (14:15)
    const end = data.expectedEndDate ? parseYmd(data.expectedEndDate) : null; // Polinaidu: 22-04-2026 (14:15)
    const today = startOfToday();

    if (data.startDate && !start) { // Polinaidu: 22-04-2026 (14:15)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid start date', path: ['startDate'] }); // Polinaidu: 22-04-2026 (14:15)
    } else if (start && data._mode !== 'edit' && start < today) { // Polinaidu: 22-04-2026 (14:15)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date cannot be in the past',
        path: ['startDate']
      });
    }
    if (data.expectedEndDate && !end) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid expected end date', path: ['expectedEndDate'] });
    } else if (start && end && end <= start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expected end date must be after start date',
        path: ['expectedEndDate']
      });
    }

    const l2 = String(data.line2 || '').trim();
    if (l2.length > 0 && l2.length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Address line 2 must be at least 3 characters or leave empty',
        path: ['line2']
      });
    }
    if (l2.length > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Maximum 100 characters',
        path: ['line2']
      });
    }

    const cap = String(data.capacity || '').trim();
    if (cap.length > 0) {
      if (!/^\d+(\.\d+)?$/.test(cap)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Target capacity must be a positive number (e.g. 40) or leave empty',
          path: ['capacity']
        });
      } else if (Number(cap) <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Target capacity must be greater than zero',
          path: ['capacity']
        });
      }
    }
    if (cap.length > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Maximum 100 characters',
        path: ['capacity']
      });
    }
  });

/** Resolve final customerName for API (FormData). */
export function resolveCustomerName(data) {
  return data.customerPreset;
}
